import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, DownloadOutline, HStack, InformationCircleOutline, rem, Spinner, Tooltip, useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { LeaderboardFilters, leaderboardQueryKey, LeaderboardResponse } from '../../../bonus-feed/features/recognition/api/hooks/leaderboards';
import { ResourcePath } from '../../../bonus-feed/features/recognition/api/hooks/ResourcePath';
import { RecognitionCount } from '../../../bonus-feed/types/user';
import { buildLeaderboardSummaryCsv, buildLeaderboardSummarySections, downloadLeaderboardSummaryCsv } from './leaderboard-summary-csv';

const PREVIEW_LIMIT = 5;

export interface LeaderboardSummaryExportData {
	isReady: boolean;
	isFetching: boolean;
	startDate: string;
	endDate: string;
	scopeType: 'company' | 'team' | 'department';
	selectedTimeframe: string;
	hashtagCount: number;
	receivedUsers: RecognitionCount[];
	givenUsers: RecognitionCount[];
	hashtagReceivers: Record<string, RecognitionCount[]>;
	hashtags: string[];
	scopeParams: Pick<LeaderboardFilters, 'team' | 'department'>;
}

interface LeaderboardSummaryExportBarProps {
	exportData: LeaderboardSummaryExportData;
}

export const LeaderboardSummaryExportBar: FC<LeaderboardSummaryExportBarProps> = ({ exportData }) => {
	const { t } = useTranslation('navigation');
	const [isExporting, setIsExporting] = useState(false);
	const isExportingRef = useRef(false);
	const exportDataRef = useRef(exportData);
	exportDataRef.current = exportData;
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const toast = useToast();
	const { track } = useAnalytics();

	const labels = useMemo(
		() => ({
			overallMostRecognized: t('leaderboard.exportSummary.overallMostRecognized'),
			overallTopGivers: t('leaderboard.exportSummary.overallTopGivers'),
			recognitionReceived: t('leaderboard.exportSummary.recognitionReceived'),
			recognitionGiven: t('leaderboard.exportSummary.recognitionGiven'),
			hashtagReceivers: (hashtag: string) => t('leaderboard.exportSummary.hashtagReceivers', { hashtag }),
			hashtagGivers: (hashtag: string) => t('leaderboard.exportSummary.hashtagGivers', { hashtag }),
		}),
		[t]
	);

	const fetchHashtagGivers = useCallback(
		async (hashtag: string, baseFilters: Omit<LeaderboardFilters, 'hashtag' | 'direction' | 'limit' | 'offset'>) => {
			const filters: LeaderboardFilters = {
				...baseFilters,
				hashtag,
				direction: 'given',
				limit: PREVIEW_LIMIT,
			};
			const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== null));

			const result = await queryClient.fetchQuery({
				queryKey: leaderboardQueryKey(filters),
				queryFn: async () => {
					const response = await http.get<LeaderboardResponse>(ResourcePath.Leaderboard, { params });
					return {
						users: response?.data.result.filter((record) => !!record.user) ?? [],
					};
				},
			});

			return result.users;
		},
		[http, queryClient]
	);

	const handleExport = useCallback(async () => {
		if (isExportingRef.current) return;

		const current = exportDataRef.current;
		isExportingRef.current = true;
		setIsExporting(true);

		track(AnalyticsEvent.UserNavbar.LeaderboardSummaryExportClick, {
			timeframe: current.selectedTimeframe,
			scopeType: current.scopeType,
			hashtagCount: current.hashtagCount,
		});

		try {
			const baseFilters: Omit<LeaderboardFilters, 'hashtag' | 'direction' | 'limit' | 'offset'> = {
				start_date: current.startDate,
				end_date: current.endDate,
				...current.scopeParams,
			};

			const hashtagGiverResults = await Promise.all(
				current.hashtags.map(async (hashtag) => {
					const users = await fetchHashtagGivers(hashtag, baseFilters);
					return [hashtag, users] as const;
				})
			);
			const hashtagGivers = Object.fromEntries(hashtagGiverResults);

			const sections = buildLeaderboardSummarySections(
				labels,
				current.receivedUsers,
				current.givenUsers,
				current.hashtagReceivers,
				hashtagGivers,
				current.hashtags
			);
			const csv = buildLeaderboardSummaryCsv(sections);
			downloadLeaderboardSummaryCsv(csv, current.startDate, current.endDate);
		} catch {
			toast({ title: t('leaderboard.exportSummaryError'), status: 'error', duration: 4000 });
		} finally {
			isExportingRef.current = false;
			setIsExporting(false);
		}
	}, [fetchHashtagGivers, labels, t, toast, track]);

	const hasAnyUsers =
		exportData.receivedUsers.length > 0 || exportData.givenUsers.length > 0 || Object.values(exportData.hashtagReceivers).some((users) => users.length > 0);

	return (
		<Box>
			<Box mx={rem(-8)} borderTop="1px solid" borderColor="border.soft" />
			<HStack justify="flex-end" align="center" spacing={rem(6)} px={rem(20)} py={rem(10)}>
				<Button
					variant="ghost"
					colorScheme="primary"
					size="xs"
					onClick={handleExport}
					isDisabled={isExporting || exportData.isFetching || !exportData.isReady || !hasAnyUsers}
					leftIcon={isExporting ? <Spinner size="xs" /> : <DownloadOutline boxSize={rem(16)} />}
				>
					{t('leaderboard.exportCsv')}
				</Button>
				<Tooltip label={t('leaderboard.exportSummaryTooltip')} placement="top" hasArrow>
					<Box as="span" display="inline-flex" color="info.icon" lineHeight={0}>
						<InformationCircleOutline boxSize={rem(16)} />
					</Box>
				</Tooltip>
			</HStack>
		</Box>
	);
};
